/**
 * KanzleiOnboardingStepper — M-OB-13 (§8.2, `architecture-stb-onboarding-consent.md`).
 *
 * Kanzlei-Empty-State auf der Mandanten-Übersicht: ein 3-Schritte-Stepper, der
 * eine frisch registrierte Kanzlei (`accountType==='kanzlei'`) OHNE Mandat
 * durch den Einstieg führt. Der Schritt-Zustand ist rein ABGELEITET (KEIN neuer
 * Store):
 *
 *   ① Kanzlei-Profil + Onboarding-Bestätigung
 *      — erledigt, wenn `advisor_profiles/{uid}.onboardingConfirmedAt` existiert.
 *      CTA verlinkt (nur per Route) auf den §5-Onboarding-Flow, der im
 *      Mandanten-Einladen-Portal lebt (M-OB-9/Wave C). KEIN Wortlaut-Träger hier.
 *   ② Mandanten einladen — CTA auf dasselbe Portal. (In diesem Empty-State immer
 *      offen, da die Seite nur bei 0 Mandaten angezeigt wird.)
 *   ③ Cockpit nutzen — Zielzustand; wird durch das Verlassen des Empty-States
 *      (erstes Mandat) erreicht, hier nur als Ausblick.
 *
 * Der aktive Schritt ist ① solange das Onboarding nicht bestätigt ist, sonst ②.
 *
 * READ-ONLY: der einzige Backend-Zugriff ist ein `getDoc` auf das eigene
 * `advisor_profiles`-Dokument (Rules erlauben get-own). Fail-safe: bei Lade-
 * fehler wird Schritt ① als offen behandelt (kein Verschlucken des Einstiegs).
 *
 * @module components/advisor/KanzleiOnboardingStepper
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { IconClients, IconUserPlus, IconCheck, IconPortal } from '../../icons';

/** Route des §5-Onboarding-/Mandanten-Einladen-Portals (Wave C liefert den Text). */
const INVITE_PORTAL_ROUTE = '/dashboard/konto/mandanten-einladen';

/** Abgeleiteter Zustand eines einzelnen Schritts. */
type StepStatus = 'done' | 'active' | 'upcoming';

interface StepView {
  /** Stabiler Schlüssel (Test-/Render-Key). */
  key: 'profile' | 'invite' | 'cockpit';
  status: StepStatus;
  title: string;
  body: string;
  /** Optionaler CTA (Label + Ziel-Route). `null` ⇒ kein Button (Ausblick-Schritt). */
  cta: { label: string; route: string } | null;
  icon: React.ReactNode;
}

/**
 * Der Kanzlei-Onboarding-Stepper.
 *
 * @returns die gerenderte Empty-State-Führung (oder ein schlanker Ladezustand,
 *   solange das eigene `advisor_profiles`-Dokument geprüft wird).
 */
const KanzleiOnboardingStepper: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Ableitungs-Quelle für Schritt ①. `null` = noch nicht geladen.
  const [onboardingConfirmed, setOnboardingConfirmed] = useState<boolean | null>(null);

  const loadProfile = useCallback(async () => {
    if (!currentUser?.uid) {
      setOnboardingConfirmed(false);
      return;
    }
    try {
      const snap = await getDoc(doc(db, 'advisor_profiles', currentUser.uid));
      const data = snap.exists() ? snap.data() : null;
      setOnboardingConfirmed(!!data?.onboardingConfirmedAt);
    } catch (err) {
      logger.error('[KanzleiOnboardingStepper] profile load failed', err);
      // Fail-safe: Schritt ① als offen behandeln (Einstieg nicht verschlucken).
      setOnboardingConfirmed(false);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  // Aktiver Schritt: ① bis Onboarding bestätigt, danach ②. Solange der Zustand
  // noch lädt (`null`), zeigen wir einen schlanken Platzhalter statt eines
  // falsch abgeleiteten Schritt-Zustands.
  if (onboardingConfirmed === null) {
    return (
      <Card as="section" className="text-center">
        <p className="py-8 text-sm text-secondary" role="status">
          {t('mandanten.kanzleiStepper.loading')}
        </p>
      </Card>
    );
  }

  const steps: StepView[] = [
    {
      key: 'profile',
      status: onboardingConfirmed ? 'done' : 'active',
      title: t('mandanten.kanzleiStepper.step1.title'),
      body: t('mandanten.kanzleiStepper.step1.body'),
      cta: onboardingConfirmed
        ? null
        : { label: t('mandanten.kanzleiStepper.step1.cta'), route: INVITE_PORTAL_ROUTE },
      icon: <IconClients size={18} aria-hidden />,
    },
    {
      key: 'invite',
      status: onboardingConfirmed ? 'active' : 'upcoming',
      title: t('mandanten.kanzleiStepper.step2.title'),
      body: t('mandanten.kanzleiStepper.step2.body'),
      cta: { label: t('mandanten.kanzleiStepper.step2.cta'), route: INVITE_PORTAL_ROUTE },
      icon: <IconUserPlus size={18} aria-hidden />,
    },
    {
      key: 'cockpit',
      status: 'upcoming',
      title: t('mandanten.kanzleiStepper.step3.title'),
      body: t('mandanten.kanzleiStepper.step3.body'),
      cta: null,
      icon: <IconPortal size={18} aria-hidden />,
    },
  ];

  return (
    <Card as="section">
      <div className="mx-auto flex max-w-2xl flex-col gap-6 py-6">
        <div className="flex flex-col gap-1.5 text-center">
          <h2 className="text-lg font-semibold text-primary">
            {t('mandanten.kanzleiStepper.title')}
          </h2>
          <p className="text-sm leading-relaxed text-secondary">
            {t('mandanten.kanzleiStepper.subtitle')}
          </p>
        </div>

        <ol className="flex flex-col gap-3">
          {steps.map((step, index) => {
            const isDone = step.status === 'done';
            const isActive = step.status === 'active';
            return (
              <li
                key={step.key}
                data-step-key={step.key}
                data-step-status={step.status}
                className={[
                  'flex items-start gap-4 rounded-card border p-4',
                  isActive
                    ? 'border-accent bg-surface-inset'
                    : 'border-default bg-surface-raised',
                  step.status === 'upcoming' ? 'opacity-70' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span
                  className={[
                    'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
                    isDone
                      ? 'bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)]'
                      : isActive
                        ? 'bg-surface-inset text-accent'
                        : 'bg-surface-inset text-secondary',
                  ].join(' ')}
                  aria-hidden
                >
                  {isDone ? <IconCheck size={18} /> : index + 1}
                </span>

                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                      {step.icon}
                      {step.title}
                    </span>
                    {isDone && (
                      <span className="rounded-full bg-[var(--tone-success-bg)] px-2 py-0.5 text-xs font-medium text-[var(--tone-success-fg)]">
                        {t('mandanten.kanzleiStepper.done')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm leading-relaxed text-secondary">{step.body}</p>
                  {step.cta && (
                    <div>
                      <Button
                        variant={isActive ? 'primary' : 'secondary'}
                        size="sm"
                        className="mt-1"
                        onClick={() => navigate(step.cta!.route)}
                      >
                        {step.cta.label}
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </Card>
  );
};

export default KanzleiOnboardingStepper;
